import { useGroupByUuidQuery } from "@/common/api/group/use-group-by-uuid.query";
import { useGroupUpdateMutation } from "@/common/api/group/use-group-update.mutation";
import { toastMutationStatus } from "@/common/helpers/toast-mutation-status";
import { CenteredLayout, CenteredLayoutVariant } from "@/components/layouts/centered-layout";
import { Form } from "@/components/ui/form";
import { UuidExistenceWrapper } from "@/components/uuid-existence-wrapper/uuid-existence-wrapper";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useLocation, useParams } from "wouter";
import { formSchema, formSchemaType, GroupFormFields } from "./_components/group-form-fields";

export function GroupUuidUpdateView() {
  const { t } = useTranslation(["ram", "common"]);
  const pageTitle = t("ram:EditGroup");
  const pageSubtitle = t("common:ReviewAndSaveChangesDescription");
  const [_, navigate] = useLocation();
  const params = useParams<{ groupUuid: string }>();
  const groupByUuidQueryInstance = useGroupByUuidQuery(params.groupUuid);
  const { data: group, isLoading } = groupByUuidQueryInstance;
  const { isPending: isMutationPending, mutateAsync } = useGroupUpdateMutation(params.groupUuid);
  const form = useForm<formSchemaType>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: "",
    },
  });

  useEffect(() => {
    if (group) {
      form.setValue("name", group.name);
    }
  }, [group, form]);

  const onSubmit = async (values: formSchemaType) => {
    await toastMutationStatus({
      mutationFn: () => mutateAsync({ ...values }),
      successMessage: t("ram:GroupHasBeenUpdated"),
      errorMessage: t("ram:ErrorUpdatingGroup"),
    });

    navigate("/");
  };

  return (
    <UuidExistenceWrapper
      uuid={params.groupUuid}
      query={useGroupByUuidQuery}
      queryResult={groupByUuidQueryInstance}
      notFoundProps={{
        backTo: "~/ram/group",
        backToLabel: t("ram:Groups"),
      }}
    >
      <CenteredLayout variant={CenteredLayoutVariant.Form} title={pageTitle} subTitle={pageSubtitle}>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            <GroupFormFields isLoading={isMutationPending || isLoading} formControl={form.control} />
          </form>
        </Form>
      </CenteredLayout>
    </UuidExistenceWrapper>
  );
}
